// Core packages
import { Injectable } from '@angular/core';

// Third party packages
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

// Custom packages
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';

@Injectable({
  providedIn: 'root'
})
export class PropertiesService {

  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) { }

  /**
   * Create a new property
   *
   * @since 1.0.0
   *
   * @param property Property data
   * @returns Observable<any>
   */
  create(property: any): Observable<any> {
    return this.apiService.apiCall('POST', 'properties', property);
  }

  /**
   * Add a contact to property phonebook
   *
   * @since 1.0.0
   */
  createPhonebookContact(propertyId: string, phonebookContact: any): Observable<any> {
    return this.apiService.apiCall('POST', `properties/${propertyId}/phonebook`, phonebookContact);
  }

  /**
   * Get all properties list of master
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPropertiesMaster(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'properties/master-all', params);
  }

  /**
   * Get users list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any, active?: string, nfcSubscriptionActive?: boolean): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus, active, nfcSubscriptionActive };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'properties', params);
  }

  /**
   * Get users list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getTagNfcList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any, active?: string): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus, active };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `properties/tag-nfcs`, params);
  }

  /**
   * Get creators list
   */
  getTaggerUsers(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<any> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }

    return this.apiService.apiCall('GET', `properties/tagger-users`, params);
  }

  /**
   * Get users list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getSuspendedList(start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'properties/suspended', params);
  }

  /**
   * Get phonebook list for given query params
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results numbers
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getPhonebookList(propertyId: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', `properties/${propertyId}/phonebook`, params);
  }

  //   /**
  //  * Get a visit of video by token
  //  *
  //  * @since 1.0.0
  //  *
  //  * @param propertyId Property Id
  //  * @param params User data
  //  * @returns Observable<any>
  //  */
  // getVideoVisitByToken(propertyId: string, token: string): Observable<any> {
  //   return this.apiService.apiCall('GET', `properties/${propertyId}/video-token/${token}`);
  // }

  // /**
  //  * Create a visit of video
  //  *
  //  * @since 1.0.0
  //  *
  //  * @param propertyId Property Id
  //  * @param params User data
  //  * @returns Observable<any>
  //  */
  // setVideoVisit(propertyId: string, params: any): Observable<any> {
  //   return this.apiService.apiCall('POST', `properties/${propertyId}/video`, params);
  // }

  /**
   * Get users list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getInStructureList(topicKey: string, start?: number, limit?: number, sort?: string, sortVersus?: string, search?: any): Observable<ListApiResponseV2> {
    const params: any = { topicKey, start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall('GET', 'properties/in-structure', params);
  }
  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getExportInStructureList(topicKey?: string): Observable<any> {
    return this.apiService.apiCall('GET', 'properties/export-in-structure', { topicKey }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getExport(companyId?: string): Observable<any> {
    return this.apiService.apiCall('GET', 'properties/export', { companyId }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getTagNfcExport(): Observable<any> {
    return this.apiService.apiCall('GET', `properties/tag-nfcs-export`, { }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }
  
  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getExportSuspended(companyId?: string): Observable<any> {
    return this.apiService.apiCall('GET', 'properties/export-suspended', { companyId }, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getDocExport(target: string, propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/${propertyId}/export/${target}`, {}, { responseType: 'blob' })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get RAS docs of property
   *
   * @since 1.0.0
   */
  getRasDocs(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/${propertyId}/ras-docs`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Export the files of all condominium documents in zip format
   *
   * @since 1.0.0
   */
  getFileExport(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/${propertyId}/export-file`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given propertyId data from back-end
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property to get
   */
   getByOldId(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/by-old-id/${propertyId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given propertyId data from back-end
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property to get
   */
  getView(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/${propertyId}/view`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get given propertyId data from back-end
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property to get
   */
  get(propertyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/${propertyId}`).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Get property's structure
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property
   */
  getStructure(endpoint: string): Observable<any> {
    return this.apiService.apiCall('GET', endpoint);
  }

  /**
   * Update property with given propertyId with given property data
   *
   * @since 1.0.0
   *
   * @param propertyId ID of property to update
   * @param data New property's data
   */
  update(propertyId: string, data: object): Observable<any> {
    return this.apiService.apiCall('PUT', `properties/${propertyId}`, data);
  }

  /**
   * Activate property
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property
   */
  activate(propertyId: string): Observable<any> {
    return this.apiService.apiCall('POST', `properties/${propertyId}/activate`);
  }

  /**
   * Update given phonebookContactId
   *
   * @since 1.0.0
   */
  updatePhonebookContact(propertyId: string, phonebookContactId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PUT', `properties/${propertyId}/phonebook/${phonebookContactId}`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update property's nfcDetails
   *
   * @since 1.0.0
   */
  updateNfcDetails(propertyId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PUT', `properties/${propertyId}/nfc`, data).pipe(
      catchError(err => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Deactivate property
   *
   * @since 1.0.0
   *
   * @param propertyId ID of the property
   */
  delete(propertyId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `properties/${propertyId}`);
  }

  /**
   * Delete permanently a property's phonebook
   *
   * @since 1.0.0
   */
  deletePhonebookContact(propertyId: string, phonebookContactId: string): Observable<any> {
    return this.apiService.apiCall('DELETE', `properties/${propertyId}/phonebook/${phonebookContactId}`);
  }


  /**
   * Get Subscriptions Revenue
   *
   * @since 1.0.0
   *
   */
  getSubscriptionsRevenue(): Observable<any> {
    return this.apiService.apiCall('GET', 'properties/subscriptions-revenue');
  }
  
  /**
   * Toggle BiPy property with given propertyId
   *
   * @since 1.0.0
   *
   * @param propertyId ID of property to update
   */
  toggleBipy(propertyId: string): Observable<any> {
    return this.apiService.apiCall('PATCH', `properties/${propertyId}/toggleBipy`);
  }

  /**
   * Assigne Tagger user
   */
  assignTagger(propertyId: string, userId: string): Observable<any> {
    return this.apiService.apiCall('PATCH', `properties/${propertyId}/assignTagger/${userId}`)
  }

  /**
   * Assigne Taggers user
   */
  assignTaggers(userId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `properties/assignTaggers/${userId}`, data)
  }

  /**
   * Toggle BiPy property with given propertyId
   *
   * @since 1.0.0
   *
   * @param propertyId ID of property to update
   */
  updateFields(propertyId: string, data: any): Observable<any> {
    return this.apiService.apiCall('PATCH', `properties/${propertyId}`, data);
  }

  /**
   * Get all properties of company
   * 
   * @since 1.0.0
   * 
   * @param companyId ID company
   */
  getCompanyProperties(companyId: string): Observable<any> {
    return this.apiService.apiCall('GET', `properties/company/${companyId}`);
  }
}
